<script lang="ts" setup>
  import { computed, onMounted, ref, watch } from "vue";
  import CardLink from "@/components/CardLink.vue";
  import { useSessionStore } from "@/stores/session";
  import { useAppStore } from "@/stores/app";
  import { useCardsStore } from "@/stores/cards";
  import MembershipTierSelect from "@/components/membership/MembershipTierSelect.vue";
  import * as amplitude from "@amplitude/analytics-browser";
  import LineSpacer from "@/components/LineSpacer.vue";
  import { useSubscriptionFlowStore } from "@/stores/subscriptionFlow";
  import { DateTime } from "luxon";
  import { MembershipApiService, type UpdateMembershipPayload } from "@/services/membershipApiService";
  import Icon from "@/components/Icon.vue";
  import useToast from "@/composables/toast";
  import { ToastType } from "@/models/app";
  import { useI18n } from "vue-i18n";
  import { MembershipTierPlanFrequency } from '@/models/membershipTierPlan';
  import type { ApiError } from '@/utils/apiClient';

  const appStore = useAppStore();
  const cardsStore = useCardsStore();
  const sessionStore = useSessionStore();
  const subscriptionFlowStore = useSubscriptionFlowStore();
  const { t } = useI18n();

  const loading = ref<boolean>(true);
  const upgrading = ref<boolean>(false);

  /** CURRENT SUBSCRIPTION */
  const currentSubscription = computed(() => {
    return sessionStore.membership?.currentSubscription;
  });
  const currentTier = computed(() => {
    return appStore.membershipTiers.find(t => t.id === currentSubscription.value?.membershipTierId);
  });
  const currentTierPlan = computed(() => {
    return currentTier.value?.membershipTierPlans.find(tp => tp.id === currentSubscription.value?.membershipTierPlanId);
  });
  const nextBillingDate = computed(() => {
    return DateTime.fromISO(currentSubscription.value!.endsAt).plus({ days: 1 });
  });
  const billingCard = computed(() => {
    let card = subscriptionFlowStore.billingCardId
      ? cardsStore.getCard(subscriptionFlowStore.billingCardId)
      : cardsStore.activeCards.find(card => card.billing);
    if (!card) {
      card = cardsStore.activeCards[ 0 ];
    }
    return card;
  });

  /** NEW TIER */
  const selectedTier = ref(appStore.membershipTiers.find((tier) => tier.id === subscriptionFlowStore.membershipTierId)!);
  const selectedTierPlan = computed(() => membershipTierPlans.value.find((plan) => plan.id === subscriptionFlowStore.membershipTierPlanId)!);
  const membershipTierPlans = computed(() => {
    return selectedTier.value.membershipTierPlans.sort((a, b) => a.price - b.price);
  });
  const hasMultipleTierPlans = computed(() => {
    return membershipTierPlans.value.length > 1;
  });

  const subtotal = computed(() => {
    if (!selectedTier.value.isFixedTier) {
      return selectedTier.value.hasNoMinimum ? 1 : (selectedTier.value.minRoundUp ?? 0);
    }
    if (!hasMultipleTierPlans.value) {
      return membershipTierPlans.value[ 0 ].price;
    }
    return selectedTierPlan.value?.price ?? 0;
  });
  const credit = ref<number>(0);
  const total = computed(() => {
    return subtotal.value - credit.value;
  });

  const totalLabelName = computed(() => {
    if (!selectedTier.value.isFixedTier) {
      return 'roundup';
    }
    if (!hasMultipleTierPlans.value) {
      return 'month';
    }
    return selectedTierPlan.value?.frequency ?? 'month';
  });

  /** Card Linking */
  function handleCardLinking() {
    upgrading.value = true;
    subscriptionFlowStore.canClose = false;
  }

  async function handleCardLinkingSuccess() {
    upgrading.value = false;
    subscriptionFlowStore.canClose = true;
    // subscriptionFlowStore.advanceStep();
  }

  function handleCardLinkingError(error: string) {
    upgrading.value = false;
    amplitude.track('Payment Failed', {
      'Error Type': error,
    });
    subscriptionFlowStore.canClose = true;
    subscriptionFlowStore.reportError(error);
  }

  function goToTierSelection() {
    subscriptionFlowStore.step = 'tier';
  }

  async function handleUpgradeClick() {
    if (!selectedTier.value) {
      return;
    }
    upgrading.value = true;
    subscriptionFlowStore.canClose = false;

    const dto = <UpdateMembershipPayload>({
      membershipTierId: selectedTier.value.id,
      membershipTierPlanId: selectedTierPlan.value.id,
      cardId: billingCard.value.id,
    });
    try {
      await MembershipApiService.update(dto);
      await sessionStore.refreshUser();

      upgrading.value = false;
      subscriptionFlowStore.canClose = true;

      amplitude.track('Membership Upgraded', {
        "Tier Name": selectedTier.value?.name ?? '',
        "Tier Price": selectedTier.value?.price < 0 ? 'Roundup' : selectedTierPlan.value?.price,
        "Tier Cycle": selectedTierPlan.value?.frequency ?? MembershipTierPlanFrequency.MONTH,
      });

      // Close the modal at the end of the upgrade
      subscriptionFlowStore.close();
      useToast(t('subscription.flow.billing.checkout.upgrade.success'), ToastType.Success);

    } catch (e) {
      console.log(e);
      amplitude.track('Payment Failed', {
        'Error Type': (e as ApiError).message,
      });
      useToast((e as ApiError).message, ToastType.Error);
      upgrading.value = false;
    }
  }

  function handleCardEditClick() {
    subscriptionFlowStore.changeBillingStep('card-select');
  }

  onMounted(async () => {
    if (currentSubscription.value) {
      loading.value = true;
      const responsePreview = await MembershipApiService.updatePreview({
        membershipTierId: selectedTier.value.id,
        membershipTierPlanId: selectedTierPlan.value.id,
      });
      credit.value = subtotal.value - (responsePreview.data?.amountToCharge ?? 0);
      loading.value = false;
    }
  });
</script>

<template>
  <!--  <SinglePageContainer class="container" style="width: 100%; max-width: 500px;">-->
  <div class="title">
    {{ $t('subscription.flow.billing.checkout.title') }}
  </div>
  <div class="tier-details">
    <MembershipTierSelect
      :changeTierFn="goToTierSelection"
      :membership-tier="selectedTier"
      :membership-tier-plan="selectedTierPlan"
    />
  </div>
  <LineSpacer/>
  <template v-if="currentSubscription">
    <div class="payment">
        <span class="title">
          {{ $t(`subscription.flow.billing.checkout.payment.title`) }}
        </span>
      <div class="card">
        <template v-if="billingCard">
          <img :src="cardsStore.getCardSystemLogo(billingCard.system)" alt="Card system" class="card-system-logo"/>
          <span class="card-digits">{{ $t('account.card.ending.digits', { digits: billingCard.lastDigits }) }}</span>
          <Icon icon="edit" @click="handleCardEditClick"></Icon>
        </template>
      </div>
    </div>
    <LineSpacer></LineSpacer>
    <div class="totals">
      <div class="t-body-2 t-bold mb-2">
        {{ $t('common.total') }}
      </div>
      <div class="subtotal">
        <span>{{ $t(`subscription.flow.billing.checkout.totals.subtotal`) }}</span>
        <span class="price">{{ $n(subtotal, 'currency') }}</span>
      </div>
      <template v-if="loading">
        <v-skeleton-loader type="text"/>
        <v-skeleton-loader type="text"/>
      </template>
      <template v-else>
        <div v-if="credit > 0" class="credit">
          <div class="d-flex flex-row align-center">
            {{ $t(`subscription.flow.billing.checkout.totals.credit`) }}
            <Icon icon="info" class="ml-2"/>
            <v-tooltip
              z-index="3000"
              activator="parent"
              location="top"
            >
              {{ $t(`subscription.flow.billing.checkout.totals.credit.tooltip`) }}
            </v-tooltip>
          </div>
          <span class="price">-{{ $n(credit, 'currency') }}</span>
        </div>
        <div class="total">
          <span>{{ $t(`subscription.flow.billing.checkout.totals.total`) }}</span>
          <span class="price">{{ $n(total, 'currency') }}</span>
        </div>
      </template>
    </div>
    <v-btn class="full" color="primary" :loading="upgrading" @click="handleUpgradeClick">
      {{ $t('common.upgrade') }}
    </v-btn>
  </template>
  <!-- New enrollment, immediate payment and card linking -->
  <template v-else>
    <div class="totals">
      <div class="total">
        <span>{{ $t(`subscription.flow.billing.checkout.totals.total`) }}</span>
        <span>{{ $n(total, 'currency') }}</span>
      </div>
      <span>{{ $t(`signup.single-page.billing.total.${totalLabelName}`) }}</span>
    </div>
    <LineSpacer/>
    <div class="card-link">
        <span class="t-body-2 t-bold">
          {{ $t('subscription.flow.billing.checkout.payment.title') }}
        </span>
      <CardLink
        :membershipTierId="selectedTier.id"
        :membershipTierPlanId="selectedTierPlan.id"
        @linking="handleCardLinking"
        @success="handleCardLinkingSuccess"
        @error="handleCardLinkingError"
      />
    </div>
  </template>
  <!--  </SinglePageContainer>-->
</template>

<style scoped lang="scss">
  .container {
    gap: 2rem;
    padding: 0;
  }

  .header {
    align-items: center;
  }

  .tier-details {
    width: 100%;
  }

  .plans {
    width: 100%;
    display: flex;
    flex-direction: column;
    gap: 0.5rem;

    .title {
      font-size: getFontSize('body-2');
      font-weight: getFontWeight('bold');
    }

    .plans-list {
      :deep(.v-input__details) {
        display: none;
      }

      .plan-radio-label {
        width: 100%;
        display: flex;
        align-items: center;
        justify-content: space-between;
        padding-right: 1rem;

        .label {
          font-size: getFontSize('body-2');
          font-weight: getFontWeight('medium');
          color: rgb(var(--v-theme-neutral-600));
        }

        .plan-price {
          display: flex;
          align-items: center;
          gap: 0.1rem;
          white-space: nowrap;

          .price {
            font-size: getFontSize('body-2');
            font-weight: getFontWeight('bold');
          }
        }
      }
    }

  }

  .payment {
    width: 100%;
    display: flex;
    flex-direction: column;
    gap: 0.5rem;

    .title {
      font-size: getFontSize('body-2');
      font-weight: getFontWeight('bold');
    }

    .card {
      width: 100%;
      display: flex;
      align-items: center;
      justify-content: space-between;
      gap: 1rem;

      background-color: rgb(var(--v-theme-neutral-100));
      border-radius: 0.5rem;
      padding: 1rem;

      img {
        width: 50px;
      }

      span {
        font-size: getFontSize('body-2');
      }

      .icon {
        cursor: pointer;
      }
    }
  }

  .totals {
    width: 100%;

    display: flex;
    flex-direction: column;
    align-items: start;
    gap: 0.5rem;

    line-height: 1rem;
    font-size: getFontSize('caption');

    & > div {
      width: 100%;
      display: flex;
      align-items: center;
      justify-content: space-between;
      font-size: getFontSize('body-2');

      &.total {
        font-weight: getFontWeight('bold');
      }

      &.credit {
        .price {
          color: rgb(var(--v-theme-success))
        }
      }
    }

    :deep(.v-skeleton-loader__text) {
      margin: 0;
    }
  }

  .card-link {
    width: 100%;

    display: flex;
    flex-direction: column;
    gap: 0.5rem;
  }
</style>

